import { create } from 'zustand';
import type { CurrentUser } from '@/types/auth';

export type AuthStatus =
  | 'bootstrapping' // resolving the session from the refresh cookie on load
  | 'authenticated'
  | 'unauthenticated';

interface AuthState {
  /** Access token kept in memory only (never localStorage) — spec §3. */
  accessToken: string | null;
  /** Epoch ms when the access token expires (used for pre-emptive refresh). */
  accessTokenExpiresAt: number | null;
  user: CurrentUser | null;
  status: AuthStatus;
  /** Set true when a refresh failed so the UI can show a "session expired" notice. */
  sessionExpired: boolean;

  setSession: (params: { accessToken: string; expiresIn: number; user: CurrentUser }) => void;
  setAccessToken: (accessToken: string, expiresIn: number) => void;
  setUser: (user: CurrentUser) => void;
  markBootstrapped: () => void;
  /** Clear all auth state. `expired` distinguishes a forced logout from a manual one. */
  clear: (reason?: 'expired' | 'logout') => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  accessTokenExpiresAt: null,
  user: null,
  status: 'bootstrapping',
  sessionExpired: false,

  setSession: ({ accessToken, expiresIn, user }) =>
    set({
      accessToken,
      accessTokenExpiresAt: Date.now() + expiresIn * 1000,
      user,
      status: 'authenticated',
      sessionExpired: false,
    }),

  setAccessToken: (accessToken, expiresIn) =>
    set({ accessToken, accessTokenExpiresAt: Date.now() + expiresIn * 1000 }),

  setUser: (user) => set({ user }),

  markBootstrapped: () =>
    set((state) => (state.status === 'bootstrapping' ? { status: 'unauthenticated' } : {})),

  clear: (reason) =>
    set({
      accessToken: null,
      accessTokenExpiresAt: null,
      user: null,
      status: 'unauthenticated',
      sessionExpired: reason === 'expired',
    }),
}));

/** Read the current access token outside React (used by the Axios interceptor). */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}
